import React, { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Grid,
  useMediaQuery,
  useTheme
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import { putClient } from "../API/clientAPI";
import { putJobOrder } from "../API/jobOrderAPI";
import { getClient } from "../API/clientAPI";
import { getTechnicians } from "../API/technicianAPI";
import { getJobOrder } from "../API/jobOrderAPI";
import { submitDocumentFieldMapper } from "../API/documentFieldMapperAPI";

import BaseFormWrapper from "../../components/shared/BaseFormWrapper";
import StepHeaderCard from "./FormComponents/StepHeaderCard";
import StepActionBar from "./FormComponents/StepActionBar";
import PageOneReviewSection from "./FormComponents/PageOneReviewSection";
import TransmittalNotesCard from "./FormComponents/TransmittalNotesCard";
import TechnicianSignatureCard from "./FormComponents/TechnicianSignatureCard";
import EditTechniciansDialog from "./FormComponents/EditTechniciansDialog";
import ClientSignatureCard from "./FormComponents/ClientSignatureCard";
import ClientSatisfactionCard from "./FormComponents/ClientSatisfactionCard";
import DocumentLinksCard from "./FormComponents/DocumentLinksCard";
import { buildGeneratedDocumentLinks } from "./FormComponents/email/buildGeneratedDocumentLinks";

// Import S3 configurations and bucket names
import {
  s3TempBucketUploader,
  TEMP_BUCKET_NAME,
} from "../Promotions/config/aws-config-temp-bucket";

import { JOB_TYPES, JOB_DESCRIPTIONS } from "../JobOrders/utils/JobOrderConstants";

function TransmittalForm() {
  const { id } = useParams();
  const [jobOrder, setJobOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [currentStep, setCurrentStep] = useState(0);
  const [reportGenerated, setReportGenerated] = useState(false);

  const STEP_INDEX = {
    REVIEW_JOB_DETAILS: 0,
    TRANSMITTAL_NOTES: 1,
    TECHNICIAN_ONLY: 2,
    CLIENT_SIGNATURE: 3,
    CLIENT_SATISFACTION: 4,
    FINAL_REVIEW: 5,
  };

  // Get user roles from localStorage (not UserContext)
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const isTechnician = userRoles.includes('operations_technician');

  const [jobOrderParameters, setjobOrderParameters] = useState({
    status: "",
    job_date: "",
    start_time: "",
    accomplished_by_technician: "",
    transmittal_notes: "",
  });

  const [client, setClient] = useState({});
  const [editedClient, setEditedClient] = useState();
  const [links, setLinks] = useState({});

  // all techs for the dialog
  const [allTechnicians, setAllTechnicians] = useState([]);
  const [editTechsOpen, setEditTechsOpen] = useState(false);
  const [editSelectedTechIds, setEditSelectedTechIds] = useState([]);

  const [technicianSignatory, setTechnicianSignatory] = useState("");
  const [satisfactionRating, setSatisfactionRating] = useState(0);
  const [satisfactionRemarks, setSatisfactionRemarks] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const fetchJobOrder = async () => {
      try {
        setIsLoading(true);
        const data = await getJobOrder(id);
        setJobOrder(data || {});
        const client = await getClient(data.client_id); // Fetch client using client_id from job order
        setClient(client);

        const technician_signed = data?.technicians?.map(({ firstname, lastname }) => `${firstname} ${lastname}`) || [];

        setjobOrderParameters((prev) => ({
          ...prev,
          status: data?.status || "",
          job_date: data?.job_date || "",
          start_time: data?.start_time || "",
          accomplished_by_technician: technician_signed || []
        }));

        setValue("status", data?.status || "", { shouldValidate: true });
        setEditedClient(data.client);
      } catch (error) {
        setErrorMessage("Error fetching job order:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) fetchJobOrder();
  }, [id]);


  const TransmittalSchema = z.object({
    // uploaded URLs
    acknowledgedSignature: z.string().min(1, "Please click Upload for Acknowledged signature."),
    accomplishedSignature: z.string().min(1, "Please click Upload for Accomplished signature."),
    // proof that Upload was clicked (not just drawn)
    acknowledgedUploaded: z.boolean().refine(v => v === true, "Please click Upload for Acknowledged signature."),
    accomplishedUploaded: z.boolean().refine(v => v === true, "Please click Upload for Accomplished signature."),
    technician_signatory: z.string().trim().min(1, "Please choose a technician signatory."),
    acknowledgedClientSignatory: z.string().trim().min(1, "Please enter the client representative."),
    satisfactionRating: z.number().min(1, "Please provide a rating."),
    status: z
      .string()
      .refine(v => v && v !== "Not Done", {
        message: 'Please set Job Order Status to something other than "Not Done".',
      }),
  }).passthrough();

  const {
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    trigger,
    register,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(TransmittalSchema),
    mode: "onChange",
    defaultValues: {
      acknowledgedSignature: "",
      accomplishedSignature: "",
      acknowledgedUploaded: false,
      accomplishedUploaded: false,
      technician_signatory: "",
      acknowledgedClientSignatory: "",
      status: "",
      satisfactionRating: 0,
    },
  });

  // Add new states for holding the signature data URLs
  const accomplishedBySignatureRef = useRef(null);
  const acknowledgedBySignatureRef = useRef(null);
  // Declare state for each signature URL
  const [accomplishedSignatureURL, setAccomplishedSignatureURL] = useState("");
  const [acknowledgedSignatureURL, setAcknowledgedSignatureURL] = useState("");

  const [acknowledgedClientSignatory, setAcknowledgedClientSignatory] = useState("");

  // Track when client signature was uploaded (to catch technician delays)
  const [clientSignatureUploadedAt, setClientSignatureUploadedAt] = useState(null);
  // Track when technician signature was uploaded
  const [technicianSignatureUploadedAt, setTechnicianSignatureUploadedAt] = useState(null);

  // Function to recursively convert true/false to True/False in an object
  const convertBooleans = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map((item) => convertBooleans(item));
    } else if (obj && typeof obj === "object") {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, convertBooleans(value)])
      );
    } else if (typeof obj === "boolean") {
      return obj ? "True" : "False"; // Convert true/false to 'True'/'False'
    }
    return obj;
  };

  const handleJObOrderChange = (field, value) => {
    setjobOrderParameters(prev => ({ ...prev, [field]: value }));
    if (field === "status") {
      setValue("status", value, { shouldValidate: true, shouldDirty: true });
      if (value !== "Not Done") clearErrors("status");
    }
  };

  // 1. derive a lookup from the raw arrays for label ↔ value mapping
  const formatKey = s => s.toLowerCase().replace(/\s+/g, "_");

  // 2. filter & sort selected job types
  const selectedJobTypes = (jobOrder?.job_types || [])
    // map back to the human-readable label
    .map(val => JOB_TYPES.find(opt => formatKey(opt) === val))
    // remove any non-matches
    .filter(Boolean)
    // alphabetical
    .sort((a, b) => a.localeCompare(b));

  // 3. same for descriptions
  const selectedJobDescriptions = (jobOrder?.job_description || [])
    .map(val => JOB_DESCRIPTIONS.find(opt => formatKey(opt) === val))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));


  // for Edit Tech Dialog
  useEffect(() => {
    // fetch once
    getTechnicians().then(data => setAllTechnicians(data || [])).catch(console.error);
  }, []);

  const openEditTechs = () => {
    const ids = (jobOrder?.technicians || []).map(t => t.id);
    setEditSelectedTechIds(ids);
    setEditTechsOpen(true);
  };

  const handleChangeTech = (index, techId) => {
    setEditSelectedTechIds((prev) =>
      prev.map((id, i) => (i === index ? techId : id))
    );
  };

  const handleAddTech = () => {
    setEditSelectedTechIds((prev) => [...prev, ""]);
  };

  const handleRemoveTech = (index) => {
    setEditSelectedTechIds((prev) => prev.filter((_, i) => i !== index));
  };

  const closeEditTechs = () => setEditTechsOpen(false);

  const saveEditTechs = async () => {
    const selected = (allTechnicians || [])
      .filter(t => editSelectedTechIds.includes(t.id))
      .map(({ id, firstname, lastname }) => ({ id, firstname, lastname }));

    try {
      await putJobOrder(jobOrder.id, { technicians: selected });
      // update local UI immediately
      setJobOrder(prev => ({ ...prev, technicians: selected }));

      // keep the names array in sync for your PDF payload
      setjobOrderParameters(prev => ({
        ...prev,
        accomplished_by_technician: selected.map(t => `${t.firstname} ${t.lastname}`)
      }));

      // ensure signatory stays valid
      const validNames = new Set(selected.map(t => `${t.firstname} ${t.lastname}`));
      const nextSignatory =
        technicianSignatory && validNames.has(technicianSignatory)
          ? technicianSignatory
          : "";

      setTechnicianSignatory(nextSignatory);
      setValue("technician_signatory", nextSignatory, { shouldValidate: true });

      setSuccessMessage("Technicians updated.");
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to update technicians.");
    } finally {
      setEditTechsOpen(false);
    }
  };

  // Temporarily store the data URL
  // Save Accomplished By signature and upload to S3
  const saveAndUploadAccomplishedSignature = async () => {
    try {
      setIsLoading(true);

      // ⏰ CAPTURE TECHNICIAN SIGNATURE UPLOAD TIMESTAMP
      const uploadTimestamp = new Date().toISOString();
      setTechnicianSignatureUploadedAt(uploadTimestamp);

      // Resize and upload the signature
      const url = await resizeAndUploadSignature(
        accomplishedBySignatureRef,
        jobOrder.id,
        "accomplished"
      );

      // Update the state with the signature URL
      setAccomplishedSignatureURL(url);
      // ✅ reflect into RHF + prove Upload was clicked
      setValue("accomplishedSignature", url, { shouldValidate: true, shouldDirty: true });
      setValue("accomplishedUploaded", true, { shouldValidate: true });
      clearErrors(["accomplishedSignature", "accomplishedUploaded"]);
      await trigger(["accomplishedSignature", "accomplishedUploaded"]);

      // 💾 IMMEDIATELY SAVE TIMESTAMP TO BACKEND (to prevent cheating)
      await putJobOrder(jobOrder.id, {
        technician_signature_uploaded_at: uploadTimestamp
      });
      // Timestamp saved (hidden from logs for security)

      setSuccessMessage("Signature successfully saved!");
    } catch (error) {
      // Handle errors and show an error message
      setErrorMessage(`Error saving signature: ${error.message}`);
      setValue("accomplishedSignature", "", { shouldValidate: true });
      setValue("accomplishedUploaded", false, { shouldValidate: true });
      setError("accomplishedUploaded", { type: "manual", message: "Please click Upload for Accomplished signature." });
      await trigger(["accomplishedSignature", "accomplishedUploaded"]);
    } finally {
      setIsLoading(false);
    }
  };

  // Clear the Accomplished By signature without affecting the button states
  const clearAccomplishedSignature = () => {
    if (accomplishedBySignatureRef.current) {
      accomplishedBySignatureRef.current.clear();
      setAccomplishedSignatureURL("");
      setValue("accomplishedSignature", "", { shouldValidate: true, shouldDirty: true });
      setValue("accomplishedUploaded", false, { shouldValidate: true });
      setError("accomplishedUploaded", { type: "manual", message: "Please click Upload for Accomplished signature." });
      trigger(["accomplishedSignature", "accomplishedUploaded"]);
    }
  };

  // Save Acknowledged By signature and upload to S3
  const saveAndUploadAcknowledgedSignature = async () => {
    try {
      setIsLoading(true);
      // Resize and upload the signature
      const url = await resizeAndUploadSignature(
        acknowledgedBySignatureRef,
        jobOrder.id,
        "acknowledged"
      );

      // ⏰ CAPTURE CLIENT SIGNATURE UPLOAD TIMESTAMP
      const uploadTimestamp = new Date().toISOString();
      setClientSignatureUploadedAt(uploadTimestamp);

      // Update the state with the signature URL
      setAcknowledgedSignatureURL(url);
      console.log("Acknowledged Signature URL:", url);

      setValue("acknowledgedSignature", url, { shouldValidate: true, shouldDirty: true });
      setValue("acknowledgedUploaded", true, { shouldValidate: true });
      clearErrors(["acknowledgedSignature", "acknowledgedUploaded"]);
      await trigger(["acknowledgedSignature", "acknowledgedUploaded"]);

      // 💾 IMMEDIATELY SAVE TIMESTAMP TO BACKEND (to prevent cheating)
      await putJobOrder(jobOrder.id, {
        client_signature_uploaded_at: uploadTimestamp
      });
      // Timestamp saved (hidden from logs for security)

      setSuccessMessage("Signature successfully saved!");
    } catch (error) {
      setErrorMessage("Error saving signature. Please try again.");
      setValue("acknowledgedSignature", "", { shouldValidate: true });
      setValue("acknowledgedUploaded", false, { shouldValidate: true });
      setError("acknowledgedUploaded", { type: "manual", message: "Please click Upload for Acknowledged signature." });
      await trigger(["acknowledgedSignature", "acknowledgedUploaded"]);
    } finally {
      setIsLoading(false);
    }
  };
  // Clear the Acknowledged By signature
  const clearAcknowledgedSignature = () => {
    if (acknowledgedBySignatureRef.current) {
      acknowledgedBySignatureRef.current.clear();
      setAcknowledgedSignatureURL("");
      setValue("acknowledgedSignature", "", { shouldValidate: true, shouldDirty: true });
      setValue("acknowledgedUploaded", false, { shouldValidate: true });
      setError("acknowledgedUploaded", { type: "manual", message: "Please click Upload for Acknowledged signature." });
      trigger(["acknowledgedSignature", "acknowledgedUploaded"]);
    }
  };

  const resizeAndUploadSignature = async (signatureRef, jobOrderId, type) => {
    // 1. grab the trimmed canvas
    if (!signatureRef.current) throw new Error("No signature canvas available");
    const canvas = signatureRef.current.getTrimmedCanvas();
    if (!canvas) throw new Error("Nothing to upload");

    // 2. draw it into a fixed‐size canvas
    const size = 200;
    const c2 = document.createElement("canvas");
    c2.width = size;
    c2.height = size;
    c2.getContext("2d").drawImage(canvas, 0, 0, size, size);

    // 3. turn into a blob, rejecting if null
    const blob = await new Promise((resolve, reject) => {
      c2.toBlob(b => (b ? resolve(b) : reject(new Error("Canvas toBlob returned null"))), "image/png");
    });

    // 4. upload
    const key = `${jobOrderId}-signature-${type}.png`;
    const params = {
      Bucket: TEMP_BUCKET_NAME,
      Key: key,
      Body: blob,
      ContentType: "image/png",
    };
    console.log("Uploading signature:", params);

    const resp = await s3TempBucketUploader.upload(params).promise();
    // AWS SDK v2 returns resp.Location; if not, you can fall back to the public URL:
    return resp.Location || `https://${params.Bucket}.s3.amazonaws.com/${params.Key}`;
  };

  // Function to format date
  // TODO: you can use date-fns for more advanced date formatting
  function formatDate(dateString) {
    try {
      const options = { year: "numeric", month: "long", day: "numeric" };
      const date = new Date(dateString);

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        throw new Error("Invalid date format");
      }

      return new Intl.DateTimeFormat("en-US", options).format(date);
    } catch (error) {
      console.error(error.message);
      return "Invalid Date"; // Fallback for invalid dates
    }
  }

  const formattedDate = formatDate(jobOrderParameters.job_date);

  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  // Determine numeric dimensions based on breakpoints
  let canvasWidth, canvasHeight;
  if (isLg) {
    canvasWidth = 300;
    canvasHeight = 350;
  } else if (isMd) {
    canvasWidth = 250;
    canvasHeight = 325;
  } else {
    canvasWidth = 200;
    canvasHeight = 300;
  }

  let ackWidth, ackHeight;
  if (isLg) {
    ackWidth = 300;
    ackHeight = 150;
  } else if (isMd) {
    ackWidth = 250;
    ackHeight = 125;
  } else {
    ackWidth = 200;
    ackHeight = 100;
  }

  const onSubmit = async (formData) => {
    setIsLoading(true);

    const updatedJobOrderParameters = {
      ...jobOrderParameters,
      accomplishedSignature: formData.accomplishedSignature,
      acknowledgedSignature: formData.acknowledgedSignature,
      acknowledgedClientSignatory: formData.acknowledgedClientSignatory,
      technician_signatory: formData.technician_signatory,
      satisfaction_rating: satisfactionRating,
      client_satisfaction_remarks: satisfactionRemarks,
      service_report_status: "Done",
      technicians: jobOrder?.technicians || [],
      accomplished_by_technician:
        jobOrderParameters?.accomplished_by_technician ||
        (jobOrder?.technicians || []).map(t => `${t.firstname} ${t.lastname}`), 
      // ⏰ TIMESTAMP TRACKING (prevents technician time-cheating)
      technician_signature_uploaded_at: technicianSignatureUploadedAt,  // When technician signs
      client_signature_uploaded_at: clientSignatureUploadedAt,  // When UPLOAD was clicked
      report_submitted_at: new Date().toISOString(),  // When SUBMIT was clicked
    };

    const { id, ...putClientParameters } = editedClient;

    //Added for Document Generation Receipts, Reports, Certificate
    try {
      // 1) transform & convert booleans
      const payload = convertBooleans({ jobOrder, updatedJobOrderParameters });
      // 2) call your PDF “field-mapper”
      const response = await submitDocumentFieldMapper(payload);
      console.log("Job order parameters submitted successfully:", response);

      setLinks(
        buildGeneratedDocumentLinks(jobOrder.id, {
          includeReport: false,
          includeCertificate: false,
          includeReceipt: true,
        })
      );

      setReportGenerated(true);
      setCurrentStep(STEP_INDEX.FINAL_REVIEW);

      setSuccessMessage("Service Report generated successfully.");

      setIsLoading(false);

      // 💾 SAVE ALL UPDATED PARAMETERS TO DATABASE (including timestamps)
      await putJobOrder(jobOrder.id, updatedJobOrderParameters);
      await putClient(id, putClientParameters);      
    } catch (error) {
      console.error("Error submitting job order parameters:", error);
      setErrorMessage(
        `Failed to generate Service Report and Documents: ${error.message}. Please check your network connection and try again.`
      );
    } finally {
      setIsLoading(false);
    }
  }

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get("returnTo");

  const onInvalid = () => {
    const first = document.querySelector(".MuiFormHelperText-root.Mui-error");
    first?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const buildStepLogEntry = (stepKey, stepLabel, timestamp) => ({
    step_key: stepKey,
    step_label: stepLabel,
    action: "NEXT_CLICKED",
    timestamp,
  });

  const logReportStep = async (stepKey, stepLabel) => {
    if (!jobOrder?.id) {
      throw new Error("Missing job order id.");
    }

    const timestamp = new Date().toISOString();

    const existingLogs = Array.isArray(jobOrder?.report_page_logs)
      ? jobOrder.report_page_logs
      : [];

    const nextLogs = [
      ...existingLogs,
      buildStepLogEntry(stepKey, stepLabel, timestamp),
    ];

    const payload = {
      report_page_logs: nextLogs,
      [`${stepKey}_completed_at`]: timestamp,
    };

    await putJobOrder(jobOrder.id, payload);

    setJobOrder((prev) => ({
      ...prev,
      ...payload,
    }));

    return timestamp;
  };

  const handleCancel = () => {
    if (returnTo) {
      navigate(returnTo);
    } else {
      navigate("/manage-service-reports");
    }
  };

  const handleClose = () => {
    navigate("/manage-service-reports");
  };

  const goBackToStep = (stepIndex) => {
    setCurrentStep(stepIndex);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goToStep = async ({
    currentStepKey,
    currentStepLabel,
    nextStepIndex,
    fieldsToValidate = [],
  }) => {
    try {
      setIsLoading(true);
      setErrorMessage("");

      if (fieldsToValidate.length > 0) {
        const isValid = await trigger(fieldsToValidate);

        if (!isValid) {
          setErrorMessage("Please complete the required fields before proceeding.");
          onInvalid();
          return;
        }
      }

      await logReportStep(currentStepKey, currentStepLabel);

      setCurrentStep(nextStepIndex);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error(`Failed to log step: ${currentStepKey}`, error);
      setErrorMessage("Failed to proceed to the next page. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextFromReviewJobDetails = async () => {
    await goToStep({
      currentStepKey: "review_job_details",
      currentStepLabel: "REVIEW JOB DETAILS",
      nextStepIndex: STEP_INDEX.TRANSMITTAL_NOTES,
      fieldsToValidate: ["status"],
    });
  };

  const handleNextFromTransmittalNotes = async () => {
    await goToStep({
      currentStepKey: "transmittal_notes",
      currentStepLabel: "TRANSMITTAL NOTES",
      nextStepIndex: STEP_INDEX.TECHNICIAN_ONLY,
      fieldsToValidate: [],
    });
  };

  const handleNextFromTechnicianOnly = async () => {
    await goToStep({
      currentStepKey: "technician_confirmation",
      currentStepLabel: "FOR TECHNICIANS ONLY",
      nextStepIndex: STEP_INDEX.CLIENT_SIGNATURE,
      fieldsToValidate: [
        "technician_signatory",
        "accomplishedSignature",
        "accomplishedUploaded",
      ],
    });
  };

  const handleNextFromClientSignature = async () => {
    await goToStep({
      currentStepKey: "client_signature",
      currentStepLabel: "FOR CLIENT ONLY - SIGNATURE",
      nextStepIndex: STEP_INDEX.CLIENT_SATISFACTION,
      fieldsToValidate: [
        "acknowledgedClientSignatory",
        "acknowledgedSignature",
        "acknowledgedUploaded",
      ],
    });
  };

  const handleNextFromClientSatisfaction = async () => {
    await goToStep({
      currentStepKey: "client_satisfaction",
      currentStepLabel: "FOR CLIENT ONLY - SATISFACTION",
      nextStepIndex: STEP_INDEX.FINAL_REVIEW,
      fieldsToValidate: ["satisfactionRating"],
    });
  };

  return (
    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon sx={{ fontSize: "40px", marginRight: "8px" }} />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="Create Service Report"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="job-order-form">
        <Grid container>
          {/* Page 1 - Basic Info Section */}
          {currentStep === STEP_INDEX.REVIEW_JOB_DETAILS && (
            <>
              <StepHeaderCard
                title="Review Job Details"
                subtitle="Review the transmittal details before proceeding."
              />

              <PageOneReviewSection
                jobOrder={jobOrder}
                client={client}
                selectedJobTypes={selectedJobTypes}
                selectedJobDescriptions={selectedJobDescriptions}
                jobOrderParameters={jobOrderParameters}
                errors={errors}
                onStatusChange={(value) => handleJObOrderChange("status", value)}
                showStatus={true}
              />

              <StepActionBar
                isLoading={isLoading}
                showBack={false}
                showCancel={true}
                showNext={true}
                onCancel={handleCancel}
                onNext={handleNextFromReviewJobDetails}
              />
            </>
          )}

          {/* Page 2 - Transmittal Notes */}
          {currentStep === STEP_INDEX.TRANSMITTAL_NOTES && (
            <>
              <StepHeaderCard
                title="Transmittal Notes"
                subtitle="Add notes for this transmittal before proceeding."
              />

              <TransmittalNotesCard
                notes={jobOrderParameters?.transmittal_notes || ""}
                onNotesChange={(value) => handleJObOrderChange("transmittal_notes", value)}
                readOnly={false}
              />

              <StepActionBar
                isLoading={isLoading}
                showBack={true}
                showCancel={true}
                showNext={true}
                onBack={() => goBackToStep(STEP_INDEX.REVIEW_JOB_DETAILS)}
                onCancel={handleCancel}
                onNext={handleNextFromTransmittalNotes}
              />
            </>
          )}

          {/* Page 3 - Technician Only Section */}
          {currentStep === STEP_INDEX.TECHNICIAN_ONLY && (
            <>
              <StepHeaderCard
                title="For Technicians Only"
                subtitle="Confirm the technician roster and sign the form."
              />
              
              <TechnicianSignatureCard
                accomplishedBySignatureRef={accomplishedBySignatureRef}
                canvasWidth={canvasWidth}
                ackHeight={ackHeight}
                technicianSignatory={technicianSignatory}
                setTechnicianSignatory={setTechnicianSignatory}
                setValue={setValue}
                clearErrors={clearErrors}
                errors={errors}
                jobOrder={jobOrder}
                clearAccomplishedSignature={clearAccomplishedSignature}
                saveAndUploadAccomplishedSignature={saveAndUploadAccomplishedSignature}
                isLoading={isLoading}
                isTechnician={isTechnician}
                technicianSignatureUploadedAt={technicianSignatureUploadedAt}
                register={register}
                openEditTechs={openEditTechs}
                jobDate={jobOrderParameters.job_date}
                startTime={jobOrderParameters.start_time}
                onJobDateChange={(val) => handleJObOrderChange("job_date", val)}
                onStartTimeChange={(val) => handleJObOrderChange("start_time", val)}
                showSchedule={true}
                readOnlySchedule={true}
                allowEditTechnicians={true}
              />

              <EditTechniciansDialog
                open={editTechsOpen}
                onClose={closeEditTechs}
                onSave={saveEditTechs}
                editedTechs={editSelectedTechIds}
                techniciansList={allTechnicians}
                onChangeTech={handleChangeTech}
                onAddTech={handleAddTech}
                onRemoveTech={handleRemoveTech}
                isLoading={isLoading}
              />

              <StepActionBar
                isLoading={isLoading}
                showBack={true}
                showCancel={true}
                showNext={true}
                onBack={() => goBackToStep(STEP_INDEX.TRANSMITTAL_NOTES)}
                onCancel={handleCancel}
                onNext={handleNextFromTechnicianOnly}
              />
            </>
          )}

          {/* Page 4 - Client Only Signature */}
          {currentStep === STEP_INDEX.CLIENT_SIGNATURE && (
            <>
              <StepHeaderCard
                title="For Client Only"
                subtitle="Please sign the form and provide your satisfaction rating on the next page."
              />

              <ClientSignatureCard
                acknowledgedClientSignatory={acknowledgedClientSignatory}
                setAcknowledgedClientSignatory={setAcknowledgedClientSignatory}
                setValue={setValue}
                clearErrors={clearErrors}
                errors={errors}
                acknowledgedBySignatureRef={acknowledgedBySignatureRef}
                canvasWidth={ackWidth}
                ackHeight={ackHeight}
                saveAndUploadAcknowledgedSignature={saveAndUploadAcknowledgedSignature}
                clearAcknowledgedSignature={clearAcknowledgedSignature}
                isLoading={isLoading}
                isTechnician={isTechnician}
                clientSignatureUploadedAt={clientSignatureUploadedAt}
              />

              <StepActionBar
                isLoading={isLoading}
                showBack={false}
                showCancel={false}
                showNext={true}
                onBack={() => goBackToStep(STEP_INDEX.TECHNICIAN_ONLY)}
                onCancel={handleCancel}
                onNext={handleNextFromClientSignature}
              />
            </>
          )}

          {/* Page 5 - Client Only Satisfaction Rating */}
          {currentStep === STEP_INDEX.CLIENT_SATISFACTION && (
            <>
              <StepHeaderCard
                title="For Client Only"
                subtitle="Your satisfaction and feedback are important to us."
              />

              <ClientSatisfactionCard
                rating={satisfactionRating}
                remarks={satisfactionRemarks}
                onRatingChange={(val) => {
                  setSatisfactionRating(val);
                  setValue("satisfactionRating", val, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                  clearErrors("satisfactionRating");
                }}
                onRemarksChange={(val) => setSatisfactionRemarks(val)}
                ratingError={errors?.satisfactionRating?.message}
              />

              <StepActionBar
                isLoading={isLoading}
                showBack={false}
                showCancel={false}
                showNext={true}
                onBack={() => goBackToStep(STEP_INDEX.CLIENT_SIGNATURE)}
                onNext={handleNextFromClientSatisfaction}
              />
            </>
          )}

          {/* Page 6 - Document Links */}
          {currentStep === STEP_INDEX.FINAL_REVIEW && (
            <>
              <StepHeaderCard
                title="Final Review & Submit"
                subtitle="Submit the report and review the generated document links."
              />

              <DocumentLinksCard
                title="Generated Documents"
                links={links}
              />

              <StepActionBar
                isLoading={isLoading}
                showBack={false}
                showCancel={!reportGenerated}
                showSubmit={!reportGenerated}
                showClose={reportGenerated}
                onCancel={handleCancel}
                onClose={handleClose}
              />
          </>
        )}
        </Grid>
      </form>
    </BaseFormWrapper>
  );
}

export default TransmittalForm;
